import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FileText, Link2, Stethoscope } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { MedicoFilaTranscript } from "@/api/db/medico";
import { orpc } from "@/client";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Pager } from "@/components/ui/pager";
import { useMedicoIgnore } from "@/hooks/use-medico";
import { usePageVisible } from "@/hooks/use-page-visible";
import { format } from "@/lib/format";
import { MedicoLabels } from "@/lib/labels/medico";
import { PollCadence } from "@/lib/poll-cadence";

import { LinkModal } from "./link-modal";
import { MetaLine, QueueRow, QueueSection } from "./queue-section";
import { ToneChip } from "./tone-chip";
import { TranscriptModal } from "./transcript-modal";

const AUTO_TITLE = /^[A-Z][a-z]{2} \d{1,2}, \d{2}:\d{2} [AP]M$/u;

function TranscriptRow({ transcript }: { transcript: MedicoFilaTranscript }) {
	const ignore = useMedicoIgnore();

	const [linking, setLinking] = useState(false);
	const [reading, setReading] = useState(false);

	const duration = MedicoLabels.duration(transcript.durationS);
	const suggested = transcript.suggestedName !== null;
	const autoTitle = AUTO_TITLE.test(transcript.title);
	const title =
		transcript.suggestedName ??
		(autoTitle ? format.recordingMoment(transcript.recordedAt) : transcript.title);

	return (
		<QueueRow>
			<div
				className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
				data-component="transcript-card"
				data-transcript={transcript.transcriptId}
			>
				<div className="flex min-w-0 flex-1 basis-64 flex-col gap-1.5">
					<p className="flex min-w-0 items-baseline gap-2">
						<span className="truncate font-display text-sm font-semibold text-foreground">
							{title}
						</span>

						{suggested && (
							<span className="shrink-0 text-xs font-medium text-primary">
								{MedicoLabels.suggestionTag}
							</span>
						)}

						{transcript.transcriptClass !== null && (
							<ToneChip
								tone={MedicoLabels.transcriptClassTone[transcript.transcriptClass]}
								className="shrink-0"
							>
								{MedicoLabels.suggestedClassPrefix}{" "}
								{MedicoLabels.transcriptClass[transcript.transcriptClass]}
							</ToneChip>
						)}
					</p>

					<MetaLine>
						{suggested && !autoTitle && <span className="truncate">{transcript.title}</span>}

						<span>{MedicoLabels.origin[transcript.origin]}</span>

						{(suggested || !autoTitle) && (
							<span className="font-mono">{format.dateTime(transcript.recordedAt)}</span>
						)}

						{!!duration && <span className="font-mono">{duration}</span>}
					</MetaLine>

					{transcript.groupSuggested !== null && (
						<p className="text-xs font-medium text-primary">
							{MedicoLabels.groupSuggested(transcript.groupSuggested.name)}
						</p>
					)}
				</div>

				<div className="flex shrink-0 items-center gap-1.5" data-slot="transcript-actions">
					<Button
						type="button"
						variant="default"
						size="sm"
						className="px-4"
						onClick={() => setLinking(true)}
					>
						<Link2 />
						Vincular
					</Button>

					<Button type="button" variant="secondary" size="sm" onClick={() => setReading(true)}>
						<FileText />
						{MedicoLabels.transcriptRead}
					</Button>

					<ConfirmationDialog
						slot="ignore-trigger"
						tone="destructive"
						title={MedicoLabels.ignoreConfirm}
						pendingLabel={MedicoLabels.transcriptIgnoring}
						isPending={ignore.isPending}
						onConfirm={() => ignore.mutate({ transcriptId: transcript.transcriptId })}
						className="rounded-full px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						{MedicoLabels.transcriptIgnore}
					</ConfirmationDialog>
				</div>
			</div>

			{linking && <LinkModal transcript={transcript} onClose={() => setLinking(false)} />}

			{reading && (
				<TranscriptModal transcriptId={transcript.transcriptId} onClose={() => setReading(false)} />
			)}
		</QueueRow>
	);
}

export function WorkQueue({ page, onPage }: { page: number; onPage: (page: number) => void }) {
	const visible = usePageVisible();
	const { data } = useQuery(
		orpc.medico.transcripts.queryOptions({
			input: { page },
			placeholderData: keepPreviousData,
			refetchInterval: () => PollCadence.pipeline({ visible }),
		}),
	);

	const items = data?.items ?? [];
	const pages = data?.pages ?? 1;
	const total = data?.total ?? 0;

	useEffect(() => {
		if (data !== undefined && page > pages) {
			onPage(1);
		}
	}, [data, page, pages, onPage]);

	const subtitle = total === 0 ? MedicoLabels.queueEmpty : MedicoLabels.queuePending(total);

	return (
		<div className="flex flex-col gap-4">
			<QueueSection title={MedicoLabels.queueTitle} subtitle={subtitle}>
				{items.length === 0 && (
					<EmptyState icon={Stethoscope} title={MedicoLabels.queueEmptyTitle} />
				)}

				{items.map((transcript) => (
					<TranscriptRow key={transcript.transcriptId} transcript={transcript} />
				))}
			</QueueSection>

			{pages > 1 && <Pager page={page} pages={pages} onPage={onPage} />}
		</div>
	);
}
